import React from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { pathFor, ROUTE_KEYS } from "@/i18n/routeMap";

const COPY = {
  en: {
    overline: "Service areas",
    body: "We serve clients across Benidorm, Alicante, Costa Blanca, Altea, Calpe, Moraira, Jávea, Dénia, Finestrat, San Juan, Torrevieja, Murcia and Valencia. Ask us by WhatsApp for delivery options and current availability.",
  },
  es: {
    overline: "Zonas de servicio",
    body: "Atendemos clientes en Benidorm, Alicante, Costa Blanca, Altea, Calpe, Moraira, Jávea, Dénia, Finestrat, San Juan, Torrevieja, Murcia y Valencia. Consúltanos por WhatsApp las opciones de entrega y disponibilidad actual.",
  },
  ru: {
    overline: "Зоны обслуживания",
    body: "Мы обслуживаем клиентов в Бенидорме, Аликанте, на Коста-Бланке, в Альтее, Кальпе, Морайре, Хавеа, Дении, Финестрате, Сан-Хуане, Торревьехе, Мурсии и Валенсии. Напишите нам в WhatsApp, чтобы уточнить доставку и доступность.",
  },
  fr: {
    overline: "Zones desservies",
    body: "Nous servons nos clients à Benidorm, Alicante, sur la Costa Blanca, à Altea, Calpe, Moraira, Jávea, Dénia, Finestrat, San Juan, Torrevieja, Murcie et Valence. Contactez-nous sur WhatsApp pour connaître les options de livraison et les disponibilités.",
  },
  de: {
    overline: "Servicegebiete",
    body: "Wir betreuen Kunden in Benidorm, Alicante, an der Costa Blanca, in Altea, Calpe, Moraira, Jávea, Dénia, Finestrat, San Juan, Torrevieja, Murcia und Valencia. Fragen Sie uns per WhatsApp nach Liefermöglichkeiten und aktueller Verfügbarkeit.",
  },
  it: {
    overline: "Aree servite",
    body: "Serviamo clienti a Benidorm, Alicante, sulla Costa Blanca, ad Altea, Calpe, Moraira, Jávea, Dénia, Finestrat, San Juan, Torrevieja, Murcia e Valencia. Contattaci su WhatsApp per le opzioni di consegna e la disponibilità attuale.",
  },
  nl: {
    overline: "Servicegebieden",
    body: "Wij bedienen klanten in Benidorm, Alicante, aan de Costa Blanca, in Altea, Calpe, Moraira, Jávea, Dénia, Finestrat, San Juan, Torrevieja, Murcia en Valencia. Vraag ons via WhatsApp naar bezorgmogelijkheden en actuele beschikbaarheid.",
  },
  pl: {
    overline: "Obsługiwane obszary",
    body: "Obsługujemy klientów w Benidorm, Alicante, na Costa Blanca, w Altei, Calpe, Morairze, Jávei, Dénii, Finestrat, San Juan, Torrevieja, Murcji i Walencji. Zapytaj nas przez WhatsApp o opcje dostawy i aktualną dostępność.",
  },
};

const PILLS = [
  "Benidorm", "Alicante", "Costa Blanca", "Altea", "Calpe", "Moraira",
  "Jávea", "Dénia", "Finestrat", "La Nucía", "San Juan", "El Campello",
  "Villajoyosa", "Santa Pola", "Torrevieja", "Orihuela Costa",
  "Guardamar del Segura", "El Albir", "Benissa", "Pilar de la Horadada", "Murcia", "Valencia",
];

const ZONE_LINKS = {
  "Benidorm": "benidorm",
  "Alicante": "alicante",
  "Altea": "altea",
  "Calpe": "calpe",
  "Moraira": "moraira",
  "Jávea": "javea",
  "Dénia": "denia",
  "Finestrat": "finestrat",
  "La Nucía": "la-nucia",
  "Villajoyosa": "villajoyosa",
  "El Campello": "el-campello",
  "San Juan": "san-juan",
  "Santa Pola": "santa-pola",
  "Torrevieja": "torrevieja",
  "Orihuela Costa": "orihuela-costa",
  "Guardamar del Segura": "guardamar-del-segura",
  "El Albir": "albir",
  "Benissa": "benissa",
  "Pilar de la Horadada": "pilar-de-la-horadada"
};

/**
 * Reusable service-area block — drop on Home, RouteDetail, Contact.
 * Visually discreet: overline + paragraph + pills row.
 */
export default function ServiceAreas({ lang = "en", areas, compact = false }) {
  const c = COPY[lang] || COPY.en;
  const list = areas?.length ? areas : PILLS;
  return (
    <section className={`border-t border-[#2A2A2A] ${compact ? "py-8" : "py-14"} px-6 md:px-10 bg-[#0C0C0C]`} data-testid="service-areas">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-overline text-[#C9A55A] mb-3">
          <MapPin size={14} /> {c.overline}
        </div>
        <p className="text-[#F5F1EB]/80 font-light leading-relaxed">{c.body}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {list.map((p) => {
            const slug = ZONE_LINKS[p];
            const style = "text-[11px] tracking-[0.14em] uppercase text-[#F5F1EB]/70 border border-[#2A2A2A] px-2.5 py-1";
            return slug ? (
              <Link key={p} to={pathFor(ROUTE_KEYS.CATALOG, lang) + "/luxury-car-rental-" + slug} className={style + " hover:border-[#C9A55A] hover:text-[#C9A55A]"}>{p}</Link>
            ) : <span key={p} className={style}>{p}</span>;
          })}
        </div>
      </div>
    </section>
  );
}
